"use client";

import { ReactElement, useContext, useEffect, useRef, useState } from "react";
import {
  Container,
  MainContent,
  Title,
  Header,
  Input,
  ChatInput,
  ChatContent,
  Option,
  SeeResults,
  Wrapper,
  RightSidebar,
  SidebarCloseButton,
  MotionWrapper,
  HeaderRightbar,
  LeftSidebar,
} from "./styled";
import AnswerComponent from "../../components/Answer";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createQuestionThunk,
  getDetailHistoryThunk,
} from "../../store/actions/chat.action";
import { ReactComponent as DoubleChevronLeft } from "../../assets/double-chevron-left.svg";
import { ReactComponent as DoubleChevronRight } from "../../assets/double-chevron-right.svg";
import { ReactComponent as SendIcon } from "../../assets/send.svg";
import { ReactComponent as ChevronLeft } from "../../assets/chevron-left.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useNewChat } from "../../context/newChatContext";
import HotelResults from "./HotelResults";
import ChatOptions from "../../components/ChatOptions";
import { HeaderContext } from "../MainLayout";
import Sidebar from "../../components/Sidebar";

interface ResponseDataHotel {
  departStartDate?: string | null;
  departEndDate?: string | null;
  brands?: string[] | null;
  hotel?: {
    city?: string | null;
    country?: string | null;
    name?: string | null;
  };
  next_message?: string;
  result_message?: string;
  confirm_message?: string;
}

interface IOption {
  id: number;
  text: string;
  type: string;
}

interface FormDataHotel {
  ci: string | null;
  co: string | null;
  brands: string[] | null;
  city: string | null;
  country: string | null;
  name: string | null;
}

interface ChatStep {
  isReplying?: boolean;
  content: string;
  type: "user" | "bot";
  options?: IOption[];
  payload?: any;
}

export default function Hotel() {
  const { setBackAction, setIsRightSideOpen, setHeaderTitle } =
    useContext(HeaderContext);
  const navigate = useNavigate();
  const params = useParams();
  const chatContentRef = useRef<HTMLDivElement>(null);
  const sendingMessage = useRef<boolean>();
  const [sendingMessageLoading, setSendingMessageLoading] = useState<boolean>();
  const [chatId, setChatId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const [messages, setMessages] = useState<ChatStep[]>([]);
  const [question, setQuestion] = useState("");
  const [detailChat, setDetailChat] = useState<
    {
      role: string;
      content: {
        type: string;
        text: string;
      }[];
    }[]
  >([]);
  const [formDataHotel, setFormDataHotel] = useState<FormDataHotel>({
    ci: null,
    co: null,
    brands: null,
    city: null,
    country: null,
    name: null,
  });
  const [payload, setPayload] = useState<FormDataHotel>();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const { isNewChat } = useNewChat();

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Fetch Chat Content
  const fetchChatContent = async () => {
    if (!params?.id) return;

    try {
      const res = await dispatch(
        getDetailHistoryThunk({ id: params?.id })
      ).unwrap();
      setDetailChat(res.fullMsg);
    } catch {
    } finally {
    }
  };

  const updateFormDataHotel = (response: ResponseDataHotel) => {
    setFormDataHotel((prevFormData) => {
      const updatedFormData: FormDataHotel = { ...prevFormData };
      // Update ci (start date) and co (end date)
      if (
        response.departStartDate !== null &&
        response.departStartDate !== undefined
      ) {
        updatedFormData.ci = response.departStartDate;
      }
      if (
        response.departEndDate !== null &&
        response.departEndDate !== undefined
      ) {
        updatedFormData.co = response.departEndDate;
      }

      // Update brands
      if (response.brands !== null && response.brands !== undefined) {
        updatedFormData.brands = response.brands;
      }

      // Update hotel information
      if (response.hotel) {
        if (response.hotel.city !== null && response.hotel.city !== undefined) {
          updatedFormData.city = response.hotel.city;
        }
        if (
          response.hotel.country !== null &&
          response.hotel.country !== undefined
        ) {
          updatedFormData.country = response.hotel.country;
        }
        if (response.hotel.name !== null && response.hotel.name !== undefined) {
          updatedFormData.name = response.hotel.name;
        }
      }

      return updatedFormData;
    });
  };

  const processDetailChat = async (
    detailChat: { role: string; content: { text: string }[] }[]
  ): Promise<ChatStep[]> => {
    const processedChat: ChatStep[] = await Promise.all(
      detailChat.map(async (item): Promise<ChatStep> => {
        if (item.role === "user") {
          return {
            type: "user",
            content: item.content[0].text,
            options: [],
            payload: null,
          };
        } else if (item.role === "assistant") {
          let parsedContent: any = {};
          try {
            parsedContent = JSON.parse(item.content[0].text);
          } catch (error) {
            console.error("Error parsing assistant content:", error);
          }

          const question =
            parsedContent.next_message ||
            parsedContent.confirm_message ||
            parsedContent.result_message ||
            item.content[0].text;

          let payload = null as any;

          if (parsedContent.result_message) {
            try {
              payload = {
                ci: parsedContent.ci || "",
                co: parsedContent.co || "",
                brands: parsedContent.brands || [],
                city: parsedContent.city || "",
                country: parsedContent.country || "",
                name: parsedContent.name || "",
              };
            } catch (error) {
              console.error("Error fetching flight results:", error);
            }
          }

          return {
            type: "bot",
            content: question,
            options: [],
            payload,
          };
        }

        return {
          type: "bot",
          content: "",
          options: [],
          payload: null,
        };
      })
    );
    return processedChat;
  };

  // Handle select option
  const handleSelectOption = async (option: IOption) => {
    if (option.type === "confirm") {
      handleFetchHotelResult(option);
      await dispatch(
        createQuestionThunk({
          question: option.text,
          chat_id: chatId,
          need_bot: false,
          bot_role: "user",
          booking_type: 2,
        })
      ).unwrap();
    }

    if (option.type === "modify") {
      setMessages((prevMsg) => [
        ...prevMsg,
        { content: option.text, type: "user" },
      ]);

      await dispatch(
        createQuestionThunk({
          question: option.text,
          chat_id: chatId,
          need_bot: false,
          bot_role: "user",
          booking_type: 2,
        })
      ).unwrap();

      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          {
            content:
              "Got it! Just tell us the new details, and we’ll update them for you",
            type: "bot",
          },
        ]);
      }, 400);

      await dispatch(
        createQuestionThunk({
          question: JSON.stringify({
            next_message:
              "Got it! Just tell us the new details, and we’ll update them for you",
          }),
          chat_id: chatId,
          need_bot: false,
          bot_role: "assistant",
          booking_type: 2,
        })
      );
    }
  };

  const handleFetchHotelResult = async (option: IOption) => {
    try {
      setMessages((prevMsg) => [
        ...prevMsg,
        { content: option.text, type: "user" },
      ]);
      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          { content: "", isReplying: true, type: "bot" },
        ]);
      }, 600);

      setTimeout(async () => {
        const updatedHistoryMessage = [
          ...messages,
          { content: option.text, type: "user" } as ChatStep,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];
        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          type: "bot",
          content: "Great! Here are the available options for you",
          payload: formDataHotel,
        };
        await dispatch(
          createQuestionThunk({
            question: JSON.stringify({
              ...formDataHotel,
              result_message: "Great! Here are the available options for you",
            }),
            chat_id: chatId,
            need_bot: false,
            bot_role: "assistant",
            booking_type: 2,
          })
        ).unwrap();
        setMessages(updatedHistoryMessage);
      }, 1200);
    } catch (e) {}
  };

  // Handle send message
  const handleSendMessage = async (subQuestion?: string) => {
    if (sendingMessage.current) return;
    sendingMessage.current = true;
    setSendingMessageLoading(true);

    if (!subQuestion) {
      setQuestion("");
    }

    const questionToSend = subQuestion || question;
    const userMessageObject = {
      isReplying: false,
      content: questionToSend,
      type: "user",
      options: [],
      payload: null,
    } as ChatStep;

    try {
      setMessages((prevMsg) => [...prevMsg, userMessageObject]);
      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          { content: "", isReplying: true, type: "bot" },
        ]);
      }, 600);
      const res = await dispatch(
        createQuestionThunk({
          question: questionToSend,
          need_bot: true,
          bot_role: "user",
          chat_id: chatId,
          booking_type: 2,
        })
      ).unwrap();
      updateFormDataHotel(res);

      if (res.chat_id) {
        setChatId(res.chat_id);
      }

      if (res.next_message) {
        const updatedHistoryMessage = [
          ...messages,
          userMessageObject,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];
        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          content: res.next_message,
          isReplying: false,
          type: "bot",
        };
        setMessages(updatedHistoryMessage);
      }

      if (res.confirm_message) {
        const updatedHistoryMessage = [
          ...messages,
          userMessageObject,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];
        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          content: res.confirm_message,
          isReplying: false,
          type: "bot",
          options: [
            { id: 1, text: "Yes, the details are correct", type: "confirm" },
            { id: 2, text: "No, I want to modify the details", type: "modify" },
          ],
        };
        setMessages(updatedHistoryMessage);
      }
    } catch (e) {
      console.log("Send message error:", e);
    } finally {
      sendingMessage.current = false;
      setSendingMessageLoading(false);
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  };

  // Use effect to scroll to bottom when content changes
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setChatId(params?.id || "");
    fetchChatContent();
  }, [params]);

  useEffect(() => {
    const updateHistory = async () => {
      const processedChat = await processDetailChat(detailChat);
      if (processedChat.length) {
        setMessages(processedChat);
      } else {
        setMessages([
          {
            type: "bot",
            content: `Greetings, distinguished guest! I'm your AI Hotel Concierge, here to turn your stay from ordinary to extra-ordinary! What can I do to make your stay absolutely magnificent today?`,
          },
        ]);
      }
    };

    updateHistory();
  }, [detailChat]);

  useEffect(() => {
    if (isNewChat) {
      setMessages([
        {
          type: "bot",
          content: `Greetings, distinguished guest! I'm your AI Hotel Concierge, here to turn your stay from ordinary to extra-ordinary! What can I do to make your stay absolutely magnificent today?`,
        },
      ]);
      setChatId(null);
    }
  }, [isNewChat]);

  useEffect(() => {
    setBackAction?.(() => {
      return () => navigate("/");
    });
    if (detailChat?.[0]?.content?.[0]?.text && !!params?.id) {
      setHeaderTitle?.(detailChat?.[0]?.content?.[0]?.text);
    } else {
      setHeaderTitle?.("New Chat");
    }

    return () => {
      setBackAction?.(undefined);
      setHeaderTitle?.(undefined);
    };
  }, [navigate, setBackAction, detailChat]);

  useEffect(() => {
    setIsRightSideOpen?.(isSidebarOpen);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleCloseRightSideBar = () => {
      setIsSidebarOpen(false);
    };

    window.addEventListener("open-left-sidebar", handleCloseRightSideBar);

    return () => {
      window.removeEventListener("open-left-sidebar", handleCloseRightSideBar);
    };
  }, [setIsSidebarOpen]);

  return (
    <MotionWrapper
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      isOpen={isSidebarOpen}
    >
      <LeftSidebar>
        <Sidebar />
      </LeftSidebar>
      <Container isOpen={isSidebarOpen}>
        <Header>
          <Title>
            {isNewChat
              ? "New Chat"
              : detailChat &&
                detailChat.length !== 0 &&
                `${detailChat[0].content[0].text.slice(0, 30)}${
                  detailChat[0].content[0].text.length > 30 ? "..." : ""
                }`}
          </Title>
          {!isSidebarOpen && payload && (
            <DoubleChevronLeft width={14} onClick={toggleSidebar} />
          )}
        </Header>
        <MainContent>
          <ChatContent ref={chatContentRef}>
            {messages &&
              messages.length !== 0 &&
              messages.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <AnswerComponent
                    type={msg.type}
                    key={index}
                    isSidebarOpen={isSidebarOpen}
                    isReplying={msg.isReplying}
                    content={msg.content}
                    options={msg.options}
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      margin: "0 8px 0 48px",
                      gap: 12,
                    }}
                  >
                    {msg.options &&
                      msg.options.length !== 0 &&
                      msg.options.map((option: IOption) => (
                        <Option
                          onClick={() => handleSelectOption(option)}
                          key={option.id}
                        >
                          {option.text}
                        </Option>
                      ))}
                    {msg.payload && (
                      <SeeResults
                        onClick={() => {
                          setPayload(msg.payload);
                          setIsSidebarOpen(true);
                        }}
                      >
                        See Results
                      </SeeResults>
                    )}
                  </div>
                </div>
              ))}
          </ChatContent>

          <ChatOptions
            topicId={2} // 1 for flights
            messages={messages}
            handleSendMessage={handleSendMessage}
          />

          <ChatInput>
            <Input
              rows={3}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Hey Kardo! Whats the current price..."
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  e.shiftKey == false &&
                  !sendingMessage.current
                ) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <button
              onClick={() => handleSendMessage()}
              disabled={!question || sendingMessageLoading}
              style={{
                background: "#FF6B6B",
                border: "none",
                color: "#fff",
                borderRadius: "20px",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <SendIcon width={20} style={{ paddingLeft: 2 }} />
            </button>
          </ChatInput>
        </MainContent>
      </Container>
      <RightSidebar isOpen={isSidebarOpen}>
        <HeaderRightbar>
          <div style={{ position: "absolute" }}>
            {isSidebarOpen && (
              <DoubleChevronRight width={14} onClick={toggleSidebar} />
            )}
          </div>
          <h3
            style={{
              margin: 0,
              textAlign: "center",
              width: "100%",
              color: "white",
            }}
          >
            Hotels
          </h3>
        </HeaderRightbar>
        {payload && <HotelResults payload={payload} />}
      </RightSidebar>
    </MotionWrapper>
  );
}
